'use client';

import { REGEX } from '@/constants';
import { TEXT } from '@/constants/TEXT';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { useBackInStock, useBackInStockAnonymous } from '@/hooks/backInStock';
import { useGetProfile } from '@/hooks/profile-queries';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { IconBell } from '@/icons/IconBell';
import IComponentProps from '@/interfaces/componentProps';
import { IBackInStockSubscribe, IProductDetails, IProductSize } from '@/interfaces/product';
import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import AddOrChangePhoneComponent from '@/components/AddOrChangePhoneComponent';
import { FormConfig, HookForm, Input } from '@/components/HookForm';
import Modal from '@/components/Modal';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import useHeaderStore from '@/store/header';

interface IProductSizes {
  sizes: IProductSize[];
  productId?: string;
  captchaId: string;
  onSelect?: (n: number) => void;
  product: IProductDetails;
  selectedVariant?: IProductSize | null;
}

const ProductSizes = ({
  className = '',
  sizes,
  productId,
  captchaId,
  product,
  selectedVariant,
  onSelect = (n: number) => {},
}: IComponentProps & IProductSizes) => {
  const authObject = useReactQuerySession();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [openPhoneAddModal, setOpenPhoneAddModal] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [selectedForNotification, setSelectedForNotification] = useState<IBackInStockSubscribe>();
  const { mutate: mutateBackInStockSubscribe } = useBackInStock(product);
  const { mutate: mutateBackInStockAnonymous } = useBackInStockAnonymous(product, captchaToken);
  const [shouldCallProfile, setShouldCallProfile] = useState<boolean>(false);
  const { data: profileData } = useGetProfile(authObject?.session, shouldCallProfile);
  const { selectedStore, myLocation } = useDeliveryOption();
  const isExpressEnabled = useHeaderStore((state) => state.isExpressEnabled);

  const notify = (productAttributeId?: string, productAttributeValueId?: string, productId?: string) => {
    const payload: IBackInStockSubscribe = {
      productId,
      productAttributeId,
      productAttributeValueId,
      phoneNumber: profileData?.PhoneNumber,
    };

    mutateBackInStockSubscribe(payload);
  };

  const choseSize = (size: IProductSize) => {
    if (size.StockQuantity > 0) {
      onSelect(size.ProductAttributeValue);
    }
  };
  const formConfig: FormConfig = {
    initialValues: { phoneNumber: '' },
    validation: {
      mobile: {
        required: 'Mobile Number is Required',
        pattern: {
          value: REGEX.VALID_PHONE,
          message: 'Mobile number is invalid.',
        },
      },
    },
  };

  const onSubmit = (data: any) => {
    const payload: IBackInStockSubscribe = {
      phoneNumber: data?.mobile,
      ...selectedForNotification,
    };
    if (captchaToken === '') {
      openNotification('danger', 'Recaptcha Error', 'Please provide number and validate recaptcha to proceed');
    } else {
      mutateBackInStockAnonymous(payload);
      setIsModalOpen(false);
    }
  };

  const onComplete = () => {
    const payload: IBackInStockSubscribe = {
      phoneNumber: profileData?.PhoneNumber,
      ...selectedForNotification,
    };
    mutateBackInStockSubscribe(payload);
    setOpenPhoneAddModal(false);
  };
  //shows message when express location exists, is toggled and at least one variant is not available but stock exists.
  const shouldShowExpressMessage =
    Boolean(myLocation.value) && isExpressEnabled && product?.Variations?.some((v) => !v.IsAvailableInProvidedStores && v.StockQuantity > 0);

  // shows message when store is selected and at least one variant is not available but stock exists.
  const shouldShowBOPISMessage = Boolean(selectedStore.Name) && sizes?.some((v) => !v.IsAvailableInProvidedStores && v.StockQuantity > 0);

  return (
    <>
      <div className="product-sizes flex flex-col">
        <div className="mb-2 text-[1.5rem] font-bold">Sizes</div>
        {shouldShowBOPISMessage && <p className="-mt-2 mb-2 italic text-gray-400">Items not in stock at {selectedStore.Name} have been blurred.</p>}
        {shouldShowExpressMessage && (
          <p className="-mt-2 mb-2 italic text-gray-400">
            Items not eligible for express delivery in you thana: {myLocation.label} have been blurred.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          {sizes?.map((productSize, index) => {
            const notAvailableInSelectedStore = selectedStore.Id && !productSize.IsAvailableInProvidedStores;
            const notAvailableForExpress = myLocation.value && isExpressEnabled && !productSize.IsAvailableInProvidedStores;
            const isOutOfStock = productSize.StockQuantity < 1;
            const sizeValue = productSize.ProductAttributeValue;
            const isSelected = selectedVariant?.ProductAttributeValue === sizeValue;

            return (
              <div
                key={productSize.ProductAttributeValue}
                id={`product-size-${sizeValue}`}
                data-testid={`size-option-${sizeValue}`}
                className={`relative flex h-12 w-20 items-center justify-center overflow-hidden rounded-[5px] border text-xl transition-all duration-300 hover:border-black ${
                  isOutOfStock || notAvailableInSelectedStore || notAvailableForExpress
                    ? 'unavailable cursor-pointer bg-gray-200 text-gray-400'
                    : 'cursor-pointer'
                } ${isSelected ? 'active border-black bg-black text-white' : 'border-gray-400'} `}
                onClick={() => {
                  if (notAvailableForExpress) {
                    openNotification('danger', 'Product', 'This product is not available for express delivery');
                    return;
                  }

                  if (notAvailableInSelectedStore) {
                    openNotification('danger', 'Product', 'This product is not available in your selected store');
                    return;
                  }

                  if (isOutOfStock) {
                    if (authObject?.isAuthenticated) {
                      if (authObject.session?.user?.mobile) {
                        notify(productSize?.ProductAttributeId, productSize?.ProductAttributeValueId, productId);
                      } else {
                        setShouldCallProfile(true);
                        setOpenPhoneAddModal(true);
                        setSelectedForNotification({
                          productId,
                          productAttributeId: productSize?.ProductAttributeId,
                          productAttributeValueId: productSize?.ProductAttributeValueId,
                        });
                      }
                    } else {
                      setIsModalOpen(true);
                      setSelectedForNotification({
                        productId,
                        productAttributeId: productSize?.ProductAttributeId,
                        productAttributeValueId: productSize?.ProductAttributeValueId,
                      });
                    }
                  } else {
                    choseSize(productSize);
                  }
                }}
              >
                {isOutOfStock && (
                  <span data-testid={`notify-me-${sizeValue}`} className="notify absolute bottom-0 left-0 right-0 top-0 cursor-pointer text-black">
                    <IconBell className="w-5" />
                  </span>
                )}
                {sizeValue}
              </div>
            );
          })}
        </div>
      </div>
      <Modal
        data-testid="back-in-stock-modal"
        className="relative p-0"
        open={isModalOpen}
        heading="Back In Stock"
        closeModal={() => setIsModalOpen(false)}
      >
        <HookForm onSubmit={onSubmit} config={formConfig} className="space-y-6">
          <Input name="mobile" label="Mobile number *" info={TEXT.BACK_TO_STOCK} data-testid="back-in-stock-phone-input" />
          <div data-testid="recaptcha-container">
            <ReCAPTCHA
              className="ml-1"
              sitekey={captchaId}
              onChange={(value) => {
                if (value) setCaptchaToken(value);
              }}
            />
          </div>
          <button data-testid="back-in-stock-submit-button" type="submit" className={`h-16 w-full bg-black text-center text-white md:w-44`}>
            Submit
          </button>
        </HookForm>
      </Modal>
      <Modal
        data-testid="add-phone-number-modal"
        className="relative p-0"
        contentClass="w-1/3"
        open={openPhoneAddModal && profileData && !profileData.PhoneNumber ? true : false}
        heading="Add Phone Number"
        closeModal={() => setOpenPhoneAddModal(false)}
      >
        <AddOrChangePhoneComponent profileData={profileData} onComplete={onComplete} data-testid="add-phone-number-component" />
      </Modal>
      <style jsx>{`
        .unavailable:before {
          content: '';
          width: 200%;
          height: 1px;
          border-top: solid 1px;
          position: absolute;
          transform: rotate(-26.5deg);
        }
        .notify {
          clip-path: polygon(0 0, 0% 100%, 100% 0);
        }
        .notify :global(.icon-bell) {
          transform: translate(40%, 0%);
          scale: 0.8;
        }
      `}</style>
    </>
  );
};

export default ProductSizes;
